import type { Directive, DirectiveBinding } from 'vue'

/**
 * v-stack-labels
 *
 * Makes PrimeVue 4 DataTables readable as cards on phones (<768px).
 *
 * PrimeVue 4 has no built-in responsive "stack" mode, and its body `<td>`s do
 * NOT carry the column header text — so CSS alone can't label the stacked
 * cells. This directive copies each column's header text into a `data-label`
 * attribute on the matching body cell (by column index). The mobile CSS in
 * `assets/datatable.css` then renders that label via `td::before`.
 *
 * Above 768px the attributes are inert (no CSS reads them), so the desktop
 * table is completely unaffected — this is purely additive.
 *
 * Usage:
 *   <DataTable v-stack-labels ...>
 *   <DataTable v-stack-labels="{ skipIndices: [0] }" ...>  // also skip col 0
 *
 * The action/kebab and selection columns are auto-detected and left unlabeled
 * (empty header, or a cell whose only control is a button/checkbox).
 */

interface StackLabelsBinding {
  /** Extra column indices to leave unlabeled (beyond the auto-detected ones). */
  skipIndices?: number[]
}

// Header cells that are icon-only / badge-only shouldn't bleed glyphs into the
// label. Clone + strip those nodes, then read the remaining text.
function headerText(th: Element): string {
  const clone = th.cloneNode(true) as HTMLElement
  clone
    .querySelectorAll('.pi, .p-badge, .badge, .p-sortable-column-icon')
    .forEach((n) => n.remove())
  return (clone.textContent || '').trim()
}

// A trailing column with no header text, or whose cell is just a button /
// checkbox, is an actions/selection column — give it an empty label so the CSS
// hides the `::before` and right-aligns the control.
function isUnlabeledColumn(td: HTMLElement, label: string, isLast: boolean): boolean {
  if (td.classList.contains('p-selection-column')) return true
  if (label.length >= 2) return false
  if (!isLast) return false
  return !!td.querySelector('button, [role="button"], .p-checkbox')
}

function applyLabels(
  el: HTMLElement,
  binding: DirectiveBinding<StackLabelsBinding | undefined>,
): void {
  const table = el.matches('table') ? el : el.querySelector('table')
  if (!table) return
  const headers = Array.from(table.querySelectorAll(':scope > thead > tr:first-child > th'))
  if (!headers.length) return
  const labels = headers.map(headerText)
  const skip = new Set(binding.value?.skipIndices ?? [])

  table.querySelectorAll(':scope > tbody > tr').forEach((tr) => {
    const cells = Array.from(tr.children) as HTMLElement[]
    cells.forEach((td, i) => {
      if (td.tagName !== 'TD') return
      const label = labels[i] ?? ''
      const last = i === cells.length - 1
      td.dataset.label = skip.has(i) || isUnlabeledColumn(td, label, last) ? '' : label
    })
  })
}

export const vStackLabels: Directive<HTMLElement, StackLabelsBinding | undefined> = {
  mounted: applyLabels,
  // PrimeVue re-renders tbody on pagination / sort / filter; re-stamp each time.
  updated: applyLabels,
}

export default vStackLabels
